import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";

export const PackagesSection = ({ packages, fetchData }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", tier: "silver", min_investment: "", max_investment: "", roi_percentage: "", duration_days: "", features: "" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSave = async () => {
    try {
      const headers = getAuthHeaders();
      const data = {
        name: form.name, tier: form.tier,
        min_investment: parseFloat(form.min_investment), max_investment: parseFloat(form.max_investment),
        roi_percentage: parseFloat(form.roi_percentage), duration_days: parseInt(form.duration_days),
        features: form.features.split("\n").filter((f) => f.trim()),
      };
      if (editing) {
        await axios.put(`${API}/admin/packages/${editing.package_id}`, data, { headers, withCredentials: true });
        toast.success("Package updated");
      } else {
        await axios.post(`${API}/admin/packages`, data, { headers, withCredentials: true });
        toast.success("Package created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save package");
    }
  };

  const handleEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: pkg.name, tier: pkg.tier,
      min_investment: pkg.min_investment.toString(), max_investment: pkg.max_investment.toString(),
      roi_percentage: pkg.roi_percentage.toString(), duration_days: pkg.duration_days.toString(),
      features: pkg.features?.join("\n") || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (packageId) => {
    if (!confirm("Deactivate this package?")) return;
    try {
      await axios.delete(`${API}/admin/packages/${packageId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Package deactivated");
      fetchData();
    } catch { toast.error("Failed to deactivate"); }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ name: "", tier: "silver", min_investment: "", max_investment: "", roi_percentage: "", duration_days: "", features: "" });
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading">Package Management</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5" data-testid="create-package-btn"><Plus className="w-4 h-4" /> Create Package</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 max-w-lg">
            <DialogHeader><DialogTitle className="text-slate-800 font-heading">{editing ? "Edit Package" : "Create Package"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Gold Package" /></div>
                <div className="space-y-2"><Label>Tier</Label>
                  <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm">
                    <option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Min Investment ($)</Label><Input type="number" value={form.min_investment} onChange={(e) => setForm({ ...form, min_investment: e.target.value })} /></div>
                <div className="space-y-2"><Label>Max Investment ($)</Label><Input type="number" value={form.max_investment} onChange={(e) => setForm({ ...form, max_investment: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>ROI (%)</Label><Input type="number" value={form.roi_percentage} onChange={(e) => setForm({ ...form, roi_percentage: e.target.value })} /></div>
                <div className="space-y-2"><Label>Duration (days)</Label><Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Features (one per line)</Label>
                <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="w-full h-20 p-3 border border-slate-200 rounded-md resize-none text-sm" placeholder={"Feature 1\nFeature 2"} />
              </div>
              <Button onClick={handleSave} className="w-full bg-amber-500 hover:bg-amber-600 text-white">{editing ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider bg-slate-50">
                <th className="px-5 py-3 font-semibold">Package</th>
                <th className="px-5 py-3 font-semibold">Tier</th>
                <th className="px-5 py-3 font-semibold">ROI</th>
                <th className="px-5 py-3 font-semibold">Range</th>
                <th className="px-5 py-3 font-semibold">Duration</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {packages.map((pkg) => (
                <tr key={pkg.package_id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-700 font-medium text-sm">{pkg.name}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${pkg.tier === "gold" ? "bg-amber-100 text-amber-700" : pkg.tier === "platinum" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600"}`}>{pkg.tier}</span>
                  </td>
                  <td className="px-5 py-3 text-emerald-600 font-mono text-sm">{pkg.roi_percentage}%</td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-xs">${pkg.min_investment.toLocaleString()} - ${pkg.max_investment.toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-600 text-sm">{pkg.duration_days} days</td>
                  <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pkg.is_active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{pkg.is_active ? "Active" : "Inactive"}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700" onClick={() => handleEdit(pkg)}><Edit className="w-3.5 h-3.5" /></Button>
                      {pkg.is_active && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:text-rose-600" onClick={() => handleDelete(pkg.package_id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
